import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output, TemplateRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { SequenceMediaComponent } from '@modules/sequence/sequence-media/sequence-media.component';
import { ScrollTrackerDirective } from '@modules/shared/directives/scroll-tracker.directive';
import { FileMediaPreviewComponent } from '@modules/shared/file-media-preview/file-media-preview.component';
import { SpinnerComponent } from '@modules/shared/spinner/spinner.component';
import { TuiCheckbox, TuiLabel, TuiRadio, TuiScrollbar, TuiTitle } from '@taiga-ui/core';
import { TuiFade } from '@taiga-ui/kit';
import { TuiBlockStatus } from '@taiga-ui/layout';
import { MediaListGroup } from '../../models/media-list-group';
import { MediaListItem } from '../../models/media-list-item';
import { MediaListItemContext } from '../../models/media-list-item-context';
import { MediaItemSourceService } from '../../services/media-item-source.service';
import { getFileKey, getMediaListItemKey } from '../../utils/media-picker-key';

@Component({
  selector: 'pxc-media-list',
  imports: [
    CommonModule,
    FormsModule,
    TranslocoDirective,
    TuiBlockStatus,
    TuiCheckbox,
    TuiFade,
    TuiRadio,
    TuiLabel,
    TuiScrollbar,
    TuiTitle,
    SequenceMediaComponent,
    FileMediaPreviewComponent,
    ScrollTrackerDirective,
    SpinnerComponent,
  ],
  templateUrl: './media-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MediaItemSourceService],
})
export class MediaListComponent {
  private readonly mediaSource = inject(MediaItemSourceService);

  protected sourceOf(item: MediaListItem): string {
    return this.mediaSource.sourceOf(item);
  }

  readonly groups = input<MediaListGroup[]>([]);
  readonly uploadedFiles = input<File[]>([]);
  readonly selectedKeys = input<Set<string>>(new Set<string>());
  readonly maxSelectable = input<number>();
  readonly itemTemplate = input<TemplateRef<MediaListItemContext>>();
  readonly isItemSelectable = input<(item: MediaListItem) => boolean>(() => true);
  readonly hasMore = input(false);
  readonly isLoadingMore = input(false);

  readonly itemToggled = output<MediaListItem>();
  readonly groupToggled = output<MediaListGroup>();
  readonly loadMoreRequest = output<void>();

  protected fileItem(file: File): MediaListItem {
    return { type: 'file', file };
  }

  protected isSelected(key: string): boolean {
    return this.selectedKeys().has(key);
  }

  protected isSelectableFn(item: MediaListItem): boolean {
    return this.isItemSelectable()(item);
  }

  protected toggle(item: MediaListItem): void {
    if (!this.isSelectableFn(item)) {
      return;
    }
    this.itemToggled.emit(item);
  }

  protected groupState(group: MediaListGroup): 'none' | 'some' | 'all' {
    const selectables = group.items.filter((item) => this.isSelectableFn(item));
    if (selectables.length === 0) {
      return 'none';
    }
    const selected = selectables.filter((item) => this.isSelected(getMediaListItemKey(item))).length;
    if (selected === 0) {
      return 'none';
    }
    return selected === selectables.length ? 'all' : 'some';
  }

  protected toggleGroup(group: MediaListGroup): void {
    this.groupToggled.emit(group);
  }

  protected readonly getFileKey = getFileKey;
  protected readonly getMediaListItemKey = getMediaListItemKey;
}
